import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Response from '@careers-wales/react-components/dist/components/utilities/Response/Response.js';
import useErrors from '@careers-wales/react-components/dist/hooks/useErrors';
import LandingView from './LandingView';
import useEventsApi from '@careers-wales/react-components/dist/hooks/api/useEventsApi.js';
import { routeTranslated } from '../../routes';
import useLanguageSwitcher from '@careers-wales/react-components/dist/hooks/useLanguageSwitcher';
import config from '../../config';
function LandingController(props) {
  const { i18n } = useTranslation();
  const { setLanguageSwitcherHref } = useLanguageSwitcher();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const { errors, setError } = useErrors();
  const { getSettings, getEvents } = useEventsApi(process.env);
  const [settings, setSettings] = useState({});
  const [events, setEvents] = useState([]);
  const [hasSettingsLoaded, setHasSettingsLoaded] = useState(false);
  const [hasEventsLoaded, setHasEventsLoaded] = useState(false);

  useEffect(() => {
    setLanguageSwitcherHref(routeTranslated('landing'));
  }); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getSettings(
      i18n.language,
      (response) => {
        setSettings(response.data);
        setHasSettingsLoaded(true);
      },
      (error) => setError(error)
    );

    getEvents(
      config.domain,
      i18n.language,
      (response) => {
        setEvents(response.data.results);
        setHasEventsLoaded(true);
      },
      (error) => setError(error)
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function filter(items, from, to) {
    if (from && to) {
      const fromMoment = moment(from, 'DD/MM/YYYY');
      const toMoment = moment(to, 'DD/MM/YYYY');

      // Filter events that have dates between from and to.
      items = items.filter((item) => {
        let hasDateInRange = false;

        item.dates.forEach((date) => {
          const dateMoment = moment(date, 'DD/MM/YYYY');

          if (
            (dateMoment.isSame(fromMoment, 'day') || dateMoment.isAfter(fromMoment, 'day')) &&
            (dateMoment.isSame(toMoment, 'day') || dateMoment.isBefore(toMoment, 'day'))
          ) {
            hasDateInRange = true;
          }
        });

        return hasDateInRange;
      });
    }

    // Filter events that have dates from today.
    items = items.filter((item) => {
      if (item.dates.length === 0 && item.hasSessions === 0) {
        return true;
      }

      let hasDateNotBeforeToday = false;

      item.dates.forEach((date) => {
        const dateMoment = moment(date, 'DD/MM/YYYY');

        if (dateMoment.isSame(moment(), 'day') || dateMoment.isAfter(moment(), 'day')) {
          hasDateNotBeforeToday = true;
        }
      });

      return hasDateNotBeforeToday;
    });

    return items;
  }

  function sort(items) {
    return items.sort((a, b) => {
      let dateA = moment(a.dates[0], 'DD/MM/YYYY');
      let dateB = moment(b.dates[0], 'DD/MM/YYYY');

      if (!dateA.isValid()) {
        return 1;
      }
      if (!dateB.isValid()) {
        return -1;
      }

      return dateB.isBefore(dateA) ? 1 : -1;
    });
  }

  function paginate(items, page) {
    if (!page || page === 1) {
      return items.slice(0, 10);
    } else {
      return items.slice((page - 1) * 10, page * 10);
    }
  }

  // Create a mutable copy of events.
  let processedEvents = [...events];

  // Filter the events between from and to and not before today.
  processedEvents = filter(events, from, to);

  // Sort the events by oldest to newest.
  processedEvents = sort(processedEvents);

  // Create a paginated subset of events.
  const paginatedEvents = paginate(processedEvents, page);

  return (
    <Response errors={errors} hasLoaded={hasSettingsLoaded && hasEventsLoaded}>
      <LandingView
        settings={settings}
        events={paginatedEvents}
        total={processedEvents.length}
        paginate={paginate}
        from={from}
        setFrom={setFrom}
        to={to}
        setTo={setTo}
        page={page}
        setPage={setPage}
      />
    </Response>
  );
}

export default LandingController;
